import type { EntrGroup, TransportResult } from '@/domain/types';
import { createApiClient, unwrap } from '@/services/api/client';
import {
  sanitizeUrlForTelemetry,
  summarizeCodeForTelemetry,
} from '@/services/errorParser';
import { getHttpErrorTelemetry, HttpError } from '@/services/http';
import { addBreadcrumb, captureException } from '@/telemetry/sentry';
import type {
  EntrGroupListResponseDto,
  EntrGroupLookupResponseDto,
} from './backendEntrApiTypes';
import {
  mapGroupListToGroupList,
  mapGroupLookupToSession,
} from './backendEntrMapper';
import { getEntrRequestHeaders } from './requestHeaders';
import type {
  EntrDataTransport,
  ListGroupsInput,
  ListGroupsOutput,
} from './types';

interface BackendEntrDataTransportOptions {
  fallback: EntrDataTransport;
}

type SessionResult = TransportResult<
  ReturnType<typeof mapGroupLookupToSession>
>;

interface SessionLookup {
  label: 'groups.detail' | 'groups.lookup';
  source: 'group' | 'ticket';
  captureTag: string;
  path: string;
  telemetry: Record<string, unknown>;
  ticketCode?: string;
  request(): Promise<unknown>;
  devFallback?(): Promise<SessionResult>;
}

function telemetryPath(path: string): string {
  return sanitizeUrlForTelemetry(
    `${import.meta.env.VITE_ENTR_API_BASE_URL || ''}/api${path}`
  );
}

function addGroupResponseBreadcrumb(
  message: string,
  dto: EntrGroupLookupResponseDto,
  source: 'group' | 'ticket'
): void {
  addBreadcrumb('entr.api', message, {
    group_id: String(dto.group_id),
    source,
    qr_present: Boolean(dto.qr_code),
    qr_code: dto.qr_code,
    member_count: dto.member_count,
    group_portrait_status: dto.group_portrait_status,
    tickets_present: Boolean(dto.tickets),
  });
}

function extractMessage(error: unknown): string | undefined {
  if (!(error instanceof HttpError)) return undefined;
  const body = error.body;
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const direct = (body as { message?: unknown }).message;
    if (typeof direct === 'string' && direct.trim().length > 0) {
      return direct;
    }
    const details = (body as { details?: unknown }).details;
    if (details && typeof details === 'object' && !Array.isArray(details)) {
      const upstream = (details as { upstream_message?: unknown })
        .upstream_message;
      if (typeof upstream === 'string' && upstream.trim().length > 0) {
        return upstream;
      }
    }
  }
  return undefined;
}

async function lookupSession(lookup: SessionLookup): Promise<SessionResult> {
  addBreadcrumb('entr.api', lookup.label, {
    ...lookup.telemetry,
    url: telemetryPath(lookup.path),
  });

  try {
    const dto = (await lookup.request()) as EntrGroupLookupResponseDto;
    addGroupResponseBreadcrumb(`${lookup.label} received`, dto, lookup.source);
    const session = mapGroupLookupToSession(
      dto,
      lookup.ticketCode ?? dto.qr_code
    );
    if (session.group.photoState === 'not-ready') {
      return { ok: false, reason: 'photos-not-ready' };
    }
    return { ok: true, value: session };
  } catch (error) {
    const message = extractMessage(error);
    if (error instanceof HttpError) {
      if (error.status === 404) {
        return { ok: false, reason: 'not-found', message };
      }
      if (error.status === 409 || error.status === 425) {
        return { ok: false, reason: 'photos-not-ready', message };
      }
    }
    captureException(error, lookup.captureTag);
    addBreadcrumb('entr.api', `${lookup.label} failed`, {
      ...lookup.telemetry,
      ...getHttpErrorTelemetry(error),
      ...(message ? { upstreamMessage: message } : {}),
    });
    if (import.meta.env.DEV) {
      if (lookup.devFallback) return lookup.devFallback();
      if (message) console.warn(`[entr.api] ${lookup.label} failed:`, message);
    }
    return { ok: false, reason: 'network-error', message };
  }
}

export function createBackendEntrDataTransport(
  options: BackendEntrDataTransportOptions
): EntrDataTransport {
  const groupsById = new Map<string, EntrGroup>();
  const api = createApiClient({
    baseUrl: import.meta.env.VITE_ENTR_API_BASE_URL || '',
    headers: () => getEntrRequestHeaders(),
  });

  return {
    async listGroups(input: ListGroupsInput): Promise<ListGroupsOutput> {
      const page = Math.max(1, Math.floor(input.page));
      const perPage = Math.max(1, Math.floor(input.pageSize));
      addBreadcrumb('entr.api', 'groups.list', { page, per_page: perPage });

      try {
        const dto = await unwrap(
          api.GET('/v1/entr/groups', {
            params: { query: { page, per_page: perPage } },
            ...api.transport({ maxRetries: 2 }),
          })
        );
        const output = mapGroupListToGroupList(
          dto as EntrGroupListResponseDto,
          input
        );
        for (const group of output.groups) {
          groupsById.set(group.id, group);
        }
        return output;
      } catch (error) {
        const message = extractMessage(error);
        captureException(error, 'entr_groups_list');
        addBreadcrumb('entr.api', 'groups.list failed', {
          page,
          per_page: perPage,
          ...getHttpErrorTelemetry(error),
          ...(message ? { upstreamMessage: message } : {}),
        });
        if (import.meta.env.DEV && message) {
          console.warn('[entr.api] groups.list failed:', message);
        }
        throw error;
      }
    },

    // Browse-grid groups legitimately arrive without a Fever QR (ENTR
    // pushes can omit QR metadata entirely) and nothing downstream needs
    // it — the printed-coverage writeback keys on group_id. No warning:
    // the groups.detail breadcrumb already records qr_present for
    // the scan-path investigations that actually care (ENTR-FRONTEND-3
    // was 400+ warning events of expected nulls).
    getSessionByGroup(groupId: string) {
      return lookupSession({
        label: 'groups.detail',
        source: 'group',
        captureTag: 'entr_groups_detail',
        path: `/v1/entr/groups/${encodeURIComponent(groupId)}`,
        telemetry: { group_id: groupId },
        request: () =>
          unwrap(
            api.GET('/v1/entr/groups/{group_id}', {
              params: { path: { group_id: groupId } },
              ...api.transport({ maxRetries: 3 }),
            })
          ),
        devFallback: () => options.fallback.getSessionByGroup(groupId),
      });
    },

    getSessionByTicketCode(ticketCode: string) {
      return lookupSession({
        label: 'groups.lookup',
        source: 'ticket',
        captureTag: 'entr_groups_lookup',
        path: `/v1/entr/groups/lookup?qr_code=${encodeURIComponent(ticketCode)}`,
        telemetry: summarizeCodeForTelemetry(ticketCode),
        ticketCode,
        request: () =>
          unwrap(
            api.GET('/v1/entr/groups/lookup', {
              params: { query: { qr_code: ticketCode } },
              ...api.transport({ maxRetries: 3 }),
            })
          ),
      });
    },

    async recordPhotoPrinted(input) {
      const amount = Math.max(1, Math.floor(input.amount));
      // Browse prints carry no ticketCode — summarize the groupId instead so
      // telemetry never derefs undefined.
      addBreadcrumb('entr.api', 'photo_printed writeback', {
        ...summarizeCodeForTelemetry(input.ticketCode ?? input.groupId ?? ''),
        amount,
      });

      try {
        const printed = await unwrap(
          api.POST('/v1/entr/photo_printed', {
            body: {
              ...(input.groupId ? { entr_group_id: input.groupId } : {}),
              amount,
              // Legacy queued items carry no key: sent without one, the
              // backend 422s them and the queue drops them as rejected.
              event_key: input.eventKey as string,
              // qr_code (and its photos_requested compat mirror) only ride
              // along for the best-effort Fever forward on ticket sessions.
              ...(input.ticketCode
                ? { qr_code: input.ticketCode, photos_requested: amount }
                : {}),
              ...(input.photoType ? { photo_type: input.photoType } : {}),
            },
            ...api.transport({ maxRetries: 0 }),
          })
        );

        return {
          ok: true,
          value: {
            groupId: String(printed.group_id),
            photosRequested: Number(printed.photos_requested),
          },
        };
      } catch (error) {
        const message = extractMessage(error);
        // 404 is now unexpected: the backend swallows a Fever qr-not-found to
        // 200 (best-effort notify). Kept as defense against an in-flight old
        // backend for one deploy cycle.
        if (error instanceof HttpError && error.status === 404) {
          return { ok: false, reason: 'not-found', message };
        }
        if (
          error instanceof HttpError &&
          (error.status === 400 || error.status === 422)
        ) {
          // The backend refused the writeback shape (e.g. the now-required
          // event_key). Retrying the identical payload can never succeed —
          // surface it as terminal so the queue drops instead of looping.
          captureException(error, 'photo_printed_writeback_rejected');
          return { ok: false, reason: 'rejected', message };
        }

        captureException(error, 'photo_printed_writeback');
        addBreadcrumb('entr.api', 'photo_printed writeback failed', {
          ...summarizeCodeForTelemetry(input.ticketCode ?? input.groupId ?? ''),
          ...getHttpErrorTelemetry(error),
          ...(message ? { upstreamMessage: message } : {}),
        });
        if (import.meta.env.DEV && message) {
          console.warn('[entr.api] photo_printed failed:', message);
        }
        return { ok: false, reason: 'network-error', message };
      }
    },
  };
}
